<template>
  <div class="fixed-asset-page">
    <div class="toolbar">
      <Button color="primary" @click="openCreate">新增资产</Button>
      <Button @click="calculateDepreciation">计提折旧</Button>
      <Button color="red" @click="deleteLastDepreciation">删除最近折旧</Button>
      <Button @click="loadStatistics">刷新统计</Button>
    </div>

    <!-- 统计卡片 -->
    <div class="statistics-cards" v-if="statistics">
      <div class="stat-card">
        <div class="stat-label">资产数量</div>
        <div class="stat-value">{{ statistics.totalCount }}</div>
      </div>
      <div class="stat-card">
        <div class="stat-label">原值合计</div>
        <div class="stat-value">¥{{ formatMoney(statistics.totalOriginalValue) }}</div>
      </div>
      <div class="stat-card">
        <div class="stat-label">累计折旧</div>
        <div class="stat-value">¥{{ formatMoney(statistics.totalAccumulatedDepreciation) }}</div>
      </div>
      <div class="stat-card">
        <div class="stat-label">净值合计</div>
        <div class="stat-value">¥{{ formatMoney(statistics.totalNetValue) }}</div>
      </div>
    </div>

    <!-- 资产列表 -->
    <Table :datas="list" :loading="loading">
      <TableItem title="资产编码" prop="assetCode" align="center"></TableItem>
      <TableItem title="资产名称" prop="assetName" align="left"></TableItem>
      <TableItem title="类别" prop="category" align="center"></TableItem>
      <TableItem title="状态" align="center">
        <template slot-scope="{data}">
          <span :class="'status-' + data.status">{{ getStatusText(data.status) }}</span>
        </template>
      </TableItem>
      <TableItem title="原值" align="right">
        <template slot-scope="{data}">
          ¥{{ formatMoney(data.originalValue) }}
        </template>
      </TableItem>
      <TableItem title="累计折旧" align="right">
        <template slot-scope="{data}">
          ¥{{ formatMoney(data.accumulatedDepreciation) }}
        </template>
      </TableItem>
      <TableItem title="净值" align="right">
        <template slot-scope="{data}">
          ¥{{ formatMoney(data.netValue) }}
        </template>
      </TableItem>
      <TableItem title="月折旧额" align="right">
        <template slot-scope="{data}">
          ¥{{ formatMoney(data.monthlyDepreciation) }}
        </template>
      </TableItem>
      <TableItem title="已提折旧月数" prop="depreciatedMonths" align="center"></TableItem>
      <TableItem title="操作" align="center">
        <template slot-scope="{data}">
          <Button text size="xs" @click="openEdit(data)">编辑</Button>
          <Button text size="xs" v-if="data.status === 0" @click="openScrap(data)">报废</Button>
          <Button text size="xs" color="red" @click="remove(data)">删除</Button>
        </template>
      </TableItem>
    </Table>

    <!-- 新增/编辑弹窗 -->
    <Modal v-model="showModal" :hasCloseIcon="true" :width="1040" class="fixed-asset-modal">
      <div class="modal-header" :class="isEdit ? 'modal-header-edit' : 'modal-header-create'">
        <div class="modal-header-text">
          <div class="modal-title">{{ isEdit ? '编辑固定资产' : '新增固定资产' }}</div>
          <div class="modal-subtitle">请填写资产基础信息和折旧参数</div>
        </div>
      </div>
      <Form ref="form" :model="form" :rules="rules" labelPosition="right" :labelWidth="120">
      <div class="form-section">
        <div class="section-title"><span class="section-icon">📋</span>基本信息</div>
        <div class="form-row">
          <FormItem label="资产编码" prop="assetCode" class="form-col">
            <input v-model="form.assetCode" placeholder="留空自动生成" />
          </FormItem>
          <FormItem label="资产名称" prop="assetName" class="form-col">
            <input v-model="form.assetName" placeholder="请输入资产名称" />
          </FormItem>
        </div>
        <div class="form-row">
          <FormItem label="资产类别" prop="category" class="form-col">
            <Select v-model="form.category" :datas="categories" placeholder="请选择类别" @change="onCategoryChange" />
          </FormItem>
          <FormItem label="规格型号" class="form-col">
            <input v-model="form.specification" placeholder="请输入规格型号" />
          </FormItem>
        </div>
      </div>
      <div class="form-section">
        <div class="section-title"><span class="section-icon">💰</span>价值信息</div>
        <div class="form-row">
          <FormItem label="购置日期" prop="purchaseDate" class="form-col">
            <DatePicker v-model="form.purchaseDate" type="date" placeholder="请选择购置日期" />
          </FormItem>
          <FormItem label="原值（元）" prop="originalValue" class="form-col">
            <input v-model.number="form.originalValue" type="number" placeholder="请输入原值" />
          </FormItem>
        </div>
        <div class="form-row">
          <FormItem label="预计使用年限（月）" prop="usefulLife" class="form-col">
            <input v-model.number="form.usefulLife" type="number" placeholder="如：60（5年）" />
          </FormItem>
          <FormItem label="净残值率（%）" class="form-col">
            <input v-model.number="form.residualRate" type="number" placeholder="如：5" />
          </FormItem>
        </div>
        <div class="form-row">
          <FormItem label="折旧方法" class="form-col">
            <Select v-model="form.depreciationMethod" :datas="depreciationMethods" placeholder="请选择折旧方法" />
          </FormItem>
          <div class="form-col"></div>
        </div>
      </div>
      <div class="form-section">
        <div class="section-title"><span class="section-icon">🏢</span>使用信息</div>
        <div class="form-row">
          <FormItem label="使用部门" class="form-col">
            <input v-model="form.department" placeholder="请输入使用部门" />
          </FormItem>
          <FormItem label="存放地点" class="form-col">
            <input v-model="form.location" placeholder="请输入存放地点" />
          </FormItem>
        </div>
        <div class="form-row">
          <FormItem label="保管人" class="form-col">
            <input v-model="form.custodian" placeholder="请输入保管人" />
          </FormItem>
          <FormItem label="供应商" class="form-col">
            <input v-model="form.supplier" placeholder="请输入供应商" />
          </FormItem>
        </div>
        <div class="form-row">
          <FormItem label="数量" class="form-col">
            <input v-model.number="form.quantity" type="number" placeholder="请输入数量" />
          </FormItem>
          <FormItem label="计量单位" class="form-col">
            <input v-model="form.unit" placeholder="如：台、套" />
          </FormItem>
        </div>
        <div class="form-row">
          <FormItem label="备注" class="form-col full-width">
            <input v-model="form.remark" placeholder="请输入备注" />
          </FormItem>
        </div>
      </div>
      </Form>
      <div slot="footer">
        <Button color="primary" @click="save" size="l">保存</Button>
        <Button @click="showModal = false" size="l">取消</Button>
      </div>
    </Modal>

    <!-- 报废弹窗 -->
    <Modal v-model="showScrapModal" title="资产报废">
      <Form :model="scrapForm">
        <FormItem label="报废备注">
          <textarea v-model="scrapForm.remark" placeholder="请输入报废原因"></textarea>
        </FormItem>
      </Form>
      <div slot="footer">
        <Button color="primary" @click="doScrap">确认报废</Button>
        <Button @click="showScrapModal = false">取消</Button>
      </div>
    </Modal>
  </div>
</template>

<script>
import fixedAssetApi from '../../api/fixedasset';
import assetCategoryApi from '../../api/assetcategory';

export default {
  name: 'FixedAssets',
  data() {
    return {
      list: [],
      loading: false,
      statistics: null,
      showModal: false,
      showScrapModal: false,
      isEdit: false,
      form: {
        assetCode: '',
        assetName: '',
        category: '',
        specification: '',
        purchaseDate: null,
        originalValue: null,
        usefulLife: null,
        residualRate: 5,
        depreciationMethod: 0,
        department: '',
        location: '',
        custodian: '',
        supplier: '',
        quantity: 1,
        unit: '台',
        remark: ''
      },
      scrapForm: {
        id: null,
        remark: ''
      },
      categories: [],
      depreciationMethods: [],
      rules: {
        assetName: [{ required: true, message: '请输入资产名称' }],
        category: [{ required: true, message: '请选择资产类别' }],
        originalValue: [{ required: true, message: '请输入原值' }],
        usefulLife: [{ required: true, message: '请输入使用年限' }]
      }
    };
  },
  mounted() {
    this.load();
    this.loadStatistics();
    this.loadCategories();
    this.loadDepreciationMethods();
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const res = await fixedAssetApi.getFixedAssets();
        this.list = res.data || [];
      } finally {
        this.loading = false;
      }
    },
    async loadStatistics() {
      try {
        const res = await fixedAssetApi.getFixedAssetStatistics();
        this.statistics = res.data;
      } catch (e) {
        console.error('加载统计失败', e);
      }
    },
    async loadCategories() {
      try {
        const res = await assetCategoryApi.getAssetCategories();
        this.categories = (res.data || []).map(c => ({ 
          title: c.name, 
          key: c.name,
          defaultUsefulLife: c.defaultUsefulLife,
          defaultResidualRate: c.defaultResidualRate,
          defaultDepreciationMethod: c.defaultDepreciationMethod
        }));
      } catch (e) {
        console.error('加载类别失败', e);
      }
    },
    onCategoryChange(categoryName) {
      const category = this.categories.find(c => c.key === categoryName);
      if (category) {
        // 自动填充默认值
        if (category.defaultUsefulLife && !this.form.usefulLife) {
          this.form.usefulLife = category.defaultUsefulLife;
        }
        if (category.defaultResidualRate && !this.form.residualRate) {
          this.form.residualRate = category.defaultResidualRate;
        }
        if (category.defaultDepreciationMethod !== undefined && !this.form.depreciationMethod) {
          this.form.depreciationMethod = category.defaultDepreciationMethod;
        }
      }
    },
    async loadDepreciationMethods() {
      try {
        const res = await fixedAssetApi.getDepreciationMethods();
        this.depreciationMethods = (res.data || []).map(m => ({ title: m.label, key: m.value }));
      } catch (e) {
        console.error('加载折旧方法失败', e);
      }
    },
    openCreate() {
      this.isEdit = false;
      this.form = {
        assetCode: '',
        assetName: '',
        category: '',
        specification: '',
        purchaseDate: null,
        originalValue: null,
        usefulLife: null,
        residualRate: 5,
        depreciationMethod: 0,
        department: '',
        location: '',
        custodian: '',
        supplier: '',
        quantity: 1,
        unit: '台',
        remark: ''
      };
      this.showModal = true;
    },
    openEdit(data) {
      this.isEdit = true;
      this.form = { ...data };
      this.showModal = true;
    },
    openScrap(data) {
      this.scrapForm = { id: data.id, remark: '' };
      this.showScrapModal = true;
    },
    async save() {
      const validResult = await this.$refs.form.valid();
      const valid = typeof validResult === 'boolean' ? validResult : validResult && validResult.result;
      if (!valid) {
        this.$Message.error('请完善固定资产必填信息');
        return;
      }

      try {
        if (this.isEdit) {
          await fixedAssetApi.updateFixedAsset(this.form.id, this.form);
        } else {
          await fixedAssetApi.createFixedAsset(this.form);
        }
        this.showModal = false;
        this.$Message.success('保存成功');
        this.load();
        this.loadStatistics();
      } catch (e) {
        this.$Message.error(e.msg || '保存失败');
      }
    },
    async doScrap() {
      if (!this.scrapForm.remark) {
        this.$Message.error('请输入报废原因');
        return;
      }
      try {
        await fixedAssetApi.scrapFixedAsset(this.scrapForm.id, this.scrapForm.remark);
        this.showScrapModal = false;
        this.$Message.success('报废成功');
        this.load();
        this.loadStatistics();
      } catch (e) {
        this.$Message.error(e.msg || '报废失败');
      }
    },
    async remove(data) {
      if (!confirm(`确认删除资产 ${data.assetName}？此操作不可恢复！`)) return;
      try {
        await fixedAssetApi.deleteFixedAsset(data.id);
        this.$Message.success('删除成功');
        this.load();
        this.loadStatistics();
      } catch (e) {
        this.$Message.error(e.msg || '删除失败');
      }
    },
    async calculateDepreciation() {
      if (!confirm('确认计提本月折旧？')) return;
      try {
        await fixedAssetApi.calculateDepreciation();
        this.$Message.success('折旧计提完成');
        this.load();
        this.loadStatistics();
      } catch (e) {
        this.$Message.error(e.msg || '计提失败');
      }
    },
    async deleteLastDepreciation() {
      if (!confirm('确认删除当前账期最近一次折旧？此操作会同步删除对应计提折旧凭证。')) return;
      try {
        const res = await fixedAssetApi.deleteLastDepreciation();
        this.$Message.success((res.data && res.data.message) || '删除最近折旧完成');
        this.load();
        this.loadStatistics();
      } catch (e) {
        this.$Message.error(e.msg || '删除最近折旧失败');
      }
    },
    getStatusText(status) {
      const map = { 0: '在用', 1: '报废', 2: '出售', 3: '盘亏' };
      return map[status] || '未知';
    },
    formatMoney(value) {
      if (value === null || value === undefined) return '0.00';
      return parseFloat(value).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }
  }
};
</script>

<style scoped>
.fixed-asset-page {
  padding: 20px;
}
.toolbar {
  margin-bottom: 16px;
}
.toolbar button {
  margin-right: 8px;
}

/* 统计卡片优化 */
.statistics-cards {
  display: flex;
  gap: 20px;
  margin-bottom: 24px;
}
.stat-card {
  flex: 1;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  padding: 20px 24px;
  color: #fff;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.35);
}
.stat-card:nth-child(2) {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  box-shadow: 0 4px 15px rgba(245, 87, 108, 0.35);
}
.stat-card:nth-child(3) {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  box-shadow: 0 4px 15px rgba(79, 172, 254, 0.35);
}
.stat-card:nth-child(4) {
  background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%);
  box-shadow: 0 4px 15px rgba(67, 233, 123, 0.35);
}
.stat-card::before {
  content: '';
  position: absolute;
  top: -50%;
  right: -50%;
  width: 100%;
  height: 100%;
  background: rgba(255,255,255,0.1);
  border-radius: 50%;
}
.stat-label {
  font-size: 14px;
  opacity: 0.9;
  margin-bottom: 8px;
  position: relative;
  z-index: 1;
}
.stat-value {
  font-size: 28px;
  font-weight: 700;
  position: relative;
  z-index: 1;
}

.status-0 { color: #52c41a; font-weight: 500; }
.status-1 { color: #999; }
.status-2 { color: #faad14; font-weight: 500; }
.status-3 { color: #f5222d; font-weight: 500; }

/* 双列表单布局优化 - 紧凑右对齐 */
.form-row {
  display: flex;
  gap: 14px;
  margin-bottom: 2px;
  align-items: flex-start;
}
.form-col {
  flex: 1;
  margin-bottom: 0;
}
.form-col :deep(.h-form-item) {
  margin-bottom: 0 !important;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  display: flex;
  align-items: flex-start;
  flex-wrap: nowrap;
  min-height: 40px;
}
.form-col :deep(.h-form-item-content) {
  flex: 1;
  min-width: 0;
}
.form-col :deep(.h-form-item-wrap) {
  min-height: 34px;
  display: flex;
  align-items: center;
}
.form-col :deep(.h-form-item-error) {
  margin-top: 1px;
  line-height: 14px;
  color: #f5222d;
  font-size: 12px;
}
.form-col :deep(.h-form-item-error-label) {
  display: none;
}
.form-col :deep(.h-form-item-label) {
  font-size: 14px;
  font-weight: normal;
  color: #595959;
  padding-right: 8px;
  text-align: right;
  white-space: nowrap;
  flex-shrink: 0;
  width: 118px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  line-height: 34px;
  margin-top: 0;
}
.form-col :deep(.h-form-item-required .h-form-item-label::before) {
  content: '* ';
  color: #ff4d4f;
  margin-right: 2px;
}
.form-col input,
.form-col :deep(.h-input),
.form-col :deep(.h-select),
.form-col :deep(.h-datetime),
.form-col :deep(.h-datetime-show) {
  width: 100%;
  height: 34px;
  font-size: 14px;
  flex: 1;
  box-sizing: border-box;
}
.form-col input,
.form-col :deep(.h-input input),
.form-col :deep(.h-datetime-show) {
  height: 34px;
  font-size: 14px;
  border-radius: 6px;
  border: 1px solid #d9d9d9;
  padding: 0 10px;
  transition: all 0.2s ease;
  background: #fafafa;
  color: #333;
  box-sizing: border-box;
}
.form-col input:focus,
.form-col :deep(.h-input input:focus),
.form-col :deep(.h-datetime-show:focus) {
  border-color: #1890ff;
  box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.1);
  background: #fff;
  outline: none;
}
.form-col input:hover,
.form-col :deep(.h-input input:hover),
.form-col :deep(.h-datetime-show:hover) {
  border-color: #40a9ff;
  background: #fff;
}
.form-col :deep(.h-select .h-select-show) {
  height: 34px !important;
  border-radius: 6px;
  border: 1px solid #d9d9d9;
  transition: all 0.2s ease;
  background: #fafafa;
  box-sizing: border-box;
}
.form-col :deep(.h-select .h-select-show:hover) {
  border-color: #40a9ff;
  background: #fff;
}
.form-col :deep(.h-select .h-select-show.h-select-focus) {
  border-color: #1890ff;
  box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.1);
  background: #fff;
}
.form-col :deep(.h-form-item-valid-error) input,
.form-col :deep(.h-form-item-valid-error .h-select-show),
.form-col :deep(.h-form-item-valid-error .h-datetime-show) {
  border-color: #f5222d;
  background: #fffafa;
}

/* 弹窗头部标题优化 */
.modal-header {
  margin: -20px -20px 10px -20px;
  padding: 12px 20px;
  display: flex;
  align-items: center;
  gap: 12px;
}
.modal-header-create {
  background: linear-gradient(135deg, #1890ff 0%, #096dd9 100%);
}
.modal-header-edit {
  background: linear-gradient(135deg, #722ed1 0%, #531dab 100%);
}
.modal-header-icon {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  color: #fff;
  flex-shrink: 0;
}
.modal-header-text {
  flex: 1;
}
.modal-title {
  font-size: 16px;
  font-weight: 600;
  color: #fff;
  line-height: 1.3;
}
.modal-subtitle {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.75);
  margin-top: 1px;
}

/* 弹窗整体样式优化 */
:deep(.fixed-asset-modal .h-modal) {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
}
:deep(.fixed-asset-modal .h-modal-body) {
  max-height: none;
  overflow-y: visible;
  padding: 0 20px 0 20px;
}
:deep(.fixed-asset-modal .h-modal-footer) {
  border-top: 1px solid #f0f0f0;
  padding: 8px 20px;
  background: #fafafa;
  display: flex;
  justify-content: center;
  gap: 12px;
}
:deep(.fixed-asset-modal .h-modal-footer .h-btn) {
  padding: 0 24px;
  height: 34px;
  font-size: 14px;
  border-radius: 6px;
  font-weight: 500;
  transition: all 0.2s ease;
  min-width: 100px;
}
:deep(.fixed-asset-modal .h-modal-footer .h-btn-primary) {
  background: linear-gradient(135deg, #1890ff, #096dd9);
  border: none;
  box-shadow: 0 4px 12px rgba(24, 144, 255, 0.35);
}
:deep(.fixed-asset-modal .h-modal-footer .h-btn-primary:hover) {
  background: linear-gradient(135deg, #40a9ff, #1890ff);
  box-shadow: 0 6px 16px rgba(24, 144, 255, 0.45);
  transform: translateY(-1px);
}
:deep(.fixed-asset-modal .h-modal-footer .h-btn:not(.h-btn-primary)) {
  background: #fff;
  border: 1px solid #d9d9d9;
  color: #595959;
}
:deep(.fixed-asset-modal .h-modal-footer .h-btn:not(.h-btn-primary):hover) {
  border-color: #1890ff;
  color: #1890ff;
}
textarea {
  width: 100%;
  min-height: 80px;
  font-size: 14px;
  border-radius: 6px;
  border: 1px solid #d9d9d9;
  padding: 8px 12px;
  resize: vertical;
  outline: none;
}
textarea:focus {
  border-color: #1890ff;
  box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.1);
}

/* 表格样式优化 */
:deep(.h-table) {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0,0,0,0.08);
}
:deep(.h-table th) {
  background: #fafafa;
  font-weight: 600;
  color: #333;
}
:deep(.h-table td) {
  padding: 14px 12px;
}

/* 表单分组样式 */
.form-section {
  margin-bottom: 4px;
  background: #fff;
  border-radius: 8px;
  border: 1px solid #f0f0f0;
  padding: 5px 12px 2px 12px;
}
.form-section:last-child {
  margin-bottom: 6px;
}
.section-title {
  font-size: 12px;
  font-weight: 600;
  color: #1890ff;
  margin-bottom: 2px;
  display: flex;
  align-items: center;
  gap: 6px;
}
.section-title::after {
  content: '';
  flex: 1;
  height: 1px;
  background: linear-gradient(to right, #e6f4ff, transparent);
  margin-left: 4px;
}
.section-icon {
  font-size: 12px;
}
.full-width {
  flex: 2 !important;
}
</style>
